import * as React from 'react'
import { ArchiveRestore, AlertTriangle } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ProfileLifecycleActions } from '@/components/contacts/ProfileLifecycleActions'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, patchJSON } from '@/lib/api'
import { hasFieldErrors } from '@/lib/contacts/errors'
import type { ProfileMembership } from '@/lib/contacts/types'
import { useViewOnly } from '@/components/templates/app-shell'

export interface ProfileActionsProps {
  actions: string[]
  contactId: number
  contactName: string
  membership?: ProfileMembership | null
  archiveImpact?: {
    membership_count: number
    affiliation_count: number
    conferred_member_count: number
    invoice_count: number
  }
  onEdit: () => void
  onLifecycleComplete: () => void
}

export function ProfileActions({
  actions,
  contactId,
  contactName,
  membership,
  archiveImpact,
  onEdit,
  onLifecycleComplete,
}: ProfileActionsProps) {
  const viewOnly = useViewOnly()
  const available = new Set(actions)
  const queryClient = useQueryClient()
  const [eraseOpen, setEraseOpen] = React.useState(false)
  const [archiveOpen, setArchiveOpen] = React.useState(false)
  const [eraseConfirmation, setEraseConfirmation] = React.useState('')
  const moreRef = React.useRef<HTMLButtonElement>(null)
  const activeMembership = membership?.status === 'Active' ? membership : null

  const archiveContact = useMutation({
    mutationFn: () => patchJSON(`/api/v1/contacts/${contactId}/archive`, {}),
    onSuccess: handleLifecycleSuccess,
  })
  const restoreContact = useMutation({
    mutationFn: () => patchJSON(`/api/v1/contacts/${contactId}/restore`, {}),
    onSuccess: handleLifecycleSuccess,
  })
  const eraseContact = useMutation({
    mutationFn: () =>
      patchJSON(`/api/v1/contacts/${contactId}/erase`, { confirmation: contactName }),
    onSuccess: handleLifecycleSuccess,
  })
  const lifecycleError = archiveContact.error ?? restoreContact.error ?? eraseContact.error
  const lifecyclePending =
    archiveContact.isPending || restoreContact.isPending || eraseContact.isPending

  if (viewOnly) return null

  async function handleLifecycleSuccess() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['person', contactId] }),
      queryClient.invalidateQueries({ queryKey: ['organization-profile', contactId] }),
    ])
    onLifecycleComplete()
  }

  function resetLifecycleMutations() {
    archiveContact.reset()
    restoreContact.reset()
    eraseContact.reset()
  }

  function openEraseDialog() {
    resetLifecycleMutations()
    setArchiveOpen(false)
    setEraseConfirmation('')
    setEraseOpen(true)
  }

  function requestArchive() {
    resetLifecycleMutations()
    if (activeMembership || archiveImpact) {
      setArchiveOpen(true)
      return
    }

    archiveContact.mutate()
  }

  function confirmArchive() {
    archiveContact.mutate()
  }

  return (
    <>
      <ProfileLifecycleActions
        triggerRef={moreRef}
        canArchive={available.has('Archive')}
        canErase={available.has('Erase')}
        pending={lifecyclePending}
        onArchive={requestArchive}
        onErase={openEraseDialog}
      />
      {available.has('Restore') && (
        <RestoreAction disabled={lifecyclePending} onRestore={() => restoreContact.mutate()} />
      )}
      {available.has('Edit') && (
        <Button type="button" onClick={onEdit}>
          Edit
        </Button>
      )}
      {lifecycleError && !eraseOpen && (
        <p role="alert" className="text-body-sm text-destructive">
          {lifecycleErrorMessage(lifecycleError)}
        </p>
      )}
      <EraseContactDialog
        open={eraseOpen}
        contactName={contactName}
        confirmation={eraseConfirmation}
        error={lifecycleError}
        pending={lifecyclePending}
        onConfirmationChange={setEraseConfirmation}
        onErase={() => eraseContact.mutate()}
        onOpenChange={setEraseOpen}
        returnFocusRef={moreRef}
      />
      <ArchiveActiveMembershipDialog
        open={archiveOpen}
        contactName={contactName}
        membership={activeMembership}
        archiveImpact={archiveImpact}
        error={archiveContact.error}
        pending={archiveContact.isPending}
        onArchive={confirmArchive}
        onOpenChange={setArchiveOpen}
        returnFocusRef={moreRef}
      />
    </>
  )
}

/** Opened from the More menu, whose item is gone by now: give focus back to More. */
function returnFocus(event: Event, target: React.RefObject<HTMLButtonElement | null>) {
  if (!target.current) return
  event.preventDefault()
  target.current.focus()
}

function RestoreAction({ disabled, onRestore }: { disabled: boolean; onRestore: () => void }) {
  return (
    <Button type="button" variant="outline" disabled={disabled} onClick={onRestore}>
      <ArchiveRestore className="mr-1.5 h-4 w-4" aria-hidden="true" />
      Restore
    </Button>
  )
}

interface ArchiveActiveMembershipDialogProps {
  open: boolean
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  contactName: string
  membership: ProfileMembership | null
  archiveImpact?: ProfileActionsProps['archiveImpact']
  error: Error | null
  pending: boolean
  onArchive: () => void
  onOpenChange: (open: boolean) => void
}

function ArchiveActiveMembershipDialog({
  open,
  contactName,
  membership,
  archiveImpact,
  error,
  pending,
  onArchive,
  onOpenChange,
  returnFocusRef,
}: ArchiveActiveMembershipDialogProps) {
  if (!membership && !archiveImpact) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onCloseAutoFocus={(event) => returnFocus(event, returnFocusRef)}>
        <DialogHeader>
          <DialogTitle>Archive {contactName}?</DialogTitle>
          <DialogDescription>
            {archiveImpact
              ? 'Archiving this Organization ends the related membership, affiliations, and conferred member status.'
              : 'Archiving hides the Contact while preserving the Active membership.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {archiveImpact ? (
            <OrganizationArchiveWarning impact={archiveImpact} />
          ) : (
            <ArchiveMembershipWarning membership={membership!} />
          )}
          {error && (
            <p role="alert" className="text-body-sm text-destructive">
              {lifecycleErrorMessage(error)}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onArchive}>
            {archiveImpact ? 'Archive organization' : 'Archive contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function OrganizationArchiveWarning({
  impact,
}: {
  impact: NonNullable<ProfileActionsProps['archiveImpact']>
}) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-body-sm text-foreground">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={1.5} />
      <div>
        <p>These changes happen after confirmation:</p>
        <ul className="list-disc pl-5 pt-1 text-muted-foreground">
          <li>{impact.membership_count} Membership ending</li>
          <li>{impact.affiliation_count} affiliations ending</li>
          <li>{impact.conferred_member_count} People losing conferred member status</li>
          <li>{impact.invoice_count} open Invoices voided</li>
          <li>Roster People remain unchanged as Contacts</li>
        </ul>
      </div>
    </div>
  )
}

function ArchiveMembershipWarning({ membership }: { membership: ProfileMembership }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-body-sm text-foreground">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={1.5} />
      <div>
        <p>These changes happen after confirmation:</p>
        <ul className="list-disc pl-5 pt-1 text-muted-foreground">
          <li>{membership.level}</li>
          <li>Membership remains Active</li>
          <li>Hidden from current member lists until the Contact is restored</li>
        </ul>
      </div>
    </div>
  )
}

interface EraseContactDialogProps {
  open: boolean
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
  contactName: string
  confirmation: string
  error: Error | null
  pending: boolean
  onConfirmationChange: (value: string) => void
  onErase: () => void
  onOpenChange: (open: boolean) => void
}

function EraseContactDialog({
  open,
  contactName,
  confirmation,
  error,
  pending,
  onConfirmationChange,
  onErase,
  onOpenChange,
  returnFocusRef,
}: EraseContactDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onCloseAutoFocus={(event) => returnFocus(event, returnFocusRef)}>
        <DialogHeader>
          <DialogTitle>Erase {contactName}?</DialogTitle>
          <DialogDescription>This cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <EraseWarning />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="erase-confirm">Type ERASE to confirm</Label>
            <Input
              id="erase-confirm"
              placeholder="ERASE"
              value={confirmation}
              onChange={(event) => onConfirmationChange(event.target.value)}
            />
          </div>
          {error && (
            <p role="alert" className="text-body-sm text-destructive">
              {lifecycleErrorMessage(error)}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={confirmation !== 'ERASE' || pending}
            onClick={onErase}
          >
            Erase contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EraseWarning() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-body-sm text-foreground">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={1.5} />
      <div>
        <p>These are permanently removed:</p>
        <ul className="list-disc pl-5 pt-1 text-muted-foreground">
          <li>Name, email, phone, and all custom field values</li>
        </ul>
        <p className="pt-1.5">Invoices, payments, and history remain as an anonymous skeleton.</p>
      </div>
    </div>
  )
}

function lifecycleErrorMessage(error: Error) {
  if (error instanceof ApiError && hasFieldErrors(error.body)) {
    return Object.values(error.body.errors).flat()[0] ?? error.message
  }

  return error.message
}
